import { useEffect } from 'react';
import { mapNavigator, type PanDirection } from '../services/MapNavigator';

/**
 * Key-to-direction mapping for arrow key panning.
 */
const KEY_DIRECTION_MAP: Record<string, PanDirection> = {
  ArrowUp: 'north',
  ArrowDown: 'south',
  ArrowRight: 'east',
  ArrowLeft: 'west',
};

/**
 * Hook that enables keyboard arrow key panning on the map.
 * Listens for ArrowUp/Down/Left/Right key events and pans the map
 * by 100px in the corresponding direction.
 *
 * The hook only activates when the map container (or document body) has focus,
 * and ignores key events when an input/textarea/select element is focused.
 *
 * **Validates: Requirements 31.4, 31.5**
 *
 * @param enabled - Whether keyboard navigation is active (default: true)
 */
export function useKeyboardNavigation(enabled: boolean = true): void {
  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't intercept when user is typing in form elements
      const target = event.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable
      ) {
        return;
      }

      const direction = KEY_DIRECTION_MAP[event.key];
      if (direction) {
        event.preventDefault();
        mapNavigator.pan(direction);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [enabled]);
}
